import React, { useState, useEffect } from 'react';
import { 
    Table, Card, Button, Modal, Form, Input, Select,
    Space, Tag, Typography, message, Tooltip, Switch
} from 'antd';
import { 
    PlusOutlined, EditOutlined, DeleteOutlined, 
    LinkOutlined, ShopOutlined, ApiOutlined,
    ExclamationCircleOutlined, CheckCircleOutlined,
    CloseCircleOutlined, CloudServerOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { 
    createShopifyStore, 
    updateShopifyStore, 
    deleteShopifyStore,
    testShopifyConnection
} from '../actions';
import { fetchWorkers } from '../../../AdminSettings/actions';

const { Text } = Typography;
const { confirm } = Modal;

const ShopifyStoreSettings = ({ shopifyStores = [], loading }) => {
    const dispatch = useDispatch();
    const [editingStore, setEditingStore] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isWorkerModalVisible, setIsWorkerModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [workerForm] = Form.useForm();
    const [testingConnection, setTestingConnection] = useState(null);

    // Get workers list from redux store
    const { workers } = useSelector(state => state.adminSettings);

    const storesArray = Array.isArray(shopifyStores) ? shopifyStores : [];

    // Fetch workers when component mounts
    useEffect(() => {
        dispatch(fetchWorkers());
    }, [dispatch]);

    const handleAdd = () => {
        setEditingStore(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingStore(record);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleSave = async (values) => {
        try {
            const result = editingStore 
                ? await dispatch(updateShopifyStore(editingStore.id, values))
                : await dispatch(createShopifyStore(values));
                
            if (result.success) {
                message.success(result.message);
                setIsModalVisible(false);
                form.resetFields();
            } else {
                message.error(result.message);
            }
        } catch (error) {
            console.error('Error saving Shopify store:', error);
            message.error('Không thể lưu Shopify store');
        }
    };

    const handleDelete = (record) => {
        confirm({
            title: 'Xác nhận xóa store',
            icon: <ExclamationCircleOutlined />,
            content: `Bạn có chắc chắn muốn xóa store "${record.storeName}" không? Hành động này không thể hoàn tác.`,
            okText: 'Xóa',
            okType: 'danger',
            cancelText: 'Hủy',
            async onOk() {
                const result = await dispatch(deleteShopifyStore(record.id));
                if (result.success) {
                    message.success(result.message);
                } else {
                    message.error(result.message);
                }
            }
        });
    };

    const handleTestConnection = async (record) => {
        setTestingConnection(record.id);
        try {
            const result = await dispatch(testShopifyConnection(record.id));
            if (result.success) {
                message.success(result.message);
            } else {
                message.error(result.message);
            }
        } catch (error) {
            message.error('Lỗi khi kiểm tra kết nối');
        } finally {
            setTestingConnection(null);
        }
    };

    const handleSelectWorker = (record) => {
        setEditingStore(record);
        workerForm.setFieldsValue({
            workerId: record.workerId || undefined
        });
        setIsWorkerModalVisible(true);
    };

    const handleWorkerAssignment = async (values) => {
        try {
            const result = await dispatch(updateShopifyStore(editingStore.id, {
                workerId: values.workerId
            }));

            if (result.success) {
                message.success('Gán worker thành công');
                setIsWorkerModalVisible(false);
                workerForm.resetFields();
            } else {
                message.error(result.message);
            }
        } catch (error) {
            console.error('Error assigning worker:', error);
            message.error('Lỗi khi gán worker');
        }
    };

    const columns = [
        {
            title: 'Store Name',
            dataIndex: 'storeName',
            key: 'storeName',
            render: (text, record) => (
                <Space>
                    <ShopOutlined />
                    <Text strong>{text}</Text>
                </Space>
            ),
        },
        {
            title: 'Domain',
            dataIndex: 'shopifyDomain',
            key: 'shopifyDomain',
            render: (text) => (
                <a href={`https://${text}`} target="_blank" rel="noopener noreferrer">
                    {text}
                </a>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
                <Space direction="vertical" size="small">
                    <Space>
                        <Tag color={record.isActive ? 'green' : 'default'}>
                            {record.isActive ? 'Active' : 'Inactive'}
                        </Tag>
                        {record.isConnected ? (
                            <Tooltip title="Connected">
                                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            </Tooltip>
                        ) : (
                            <Tooltip title="Disconnected">
                                <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                            </Tooltip>
                        )}
                    </Space>
                    {record.tokenStatus && (
                        <Tag color={
                            record.tokenStatus === 'active' ? 'success' : 
                            record.tokenStatus === 'invalid' ? 'error' : 
                            record.tokenStatus === 'expired' ? 'warning' : 'default'
                        }>
                            Token: {record.tokenStatus}
                        </Tag>
                    )}
                    {record.tokenErrorCount > 0 && (
                        <Text type="danger" style={{ fontSize: 11 }}>
                            Errors: {record.tokenErrorCount}
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            title: 'Auto Sync',
            key: 'autoSync',
            render: (_, record) => (
                <Space direction="vertical" size="small">
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Orders: {record.autoSyncOrders ? 'Yes' : 'No'}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Products: {record.autoSyncProducts ? 'Yes' : 'No'}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Last Sync',
            key: 'syncInfo',
            render: (_, record) => (
                <Space direction="vertical" size="small">
                    <Tooltip title="Last order/product sync">
                        <Text style={{ fontSize: 12 }}>
                            Sync: {record.lastSyncAt ? new Date(record.lastSyncAt).toLocaleString() : '-'}
                        </Text>
                    </Tooltip>
                    {record.lastTokenCheck && (
                        <Tooltip title="Last token validation check">
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                Token: {new Date(record.lastTokenCheck).toLocaleString()}
                            </Text>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: 'Worker',
            dataIndex: 'worker',
            key: 'worker',
            width: 250,
            render: (worker, record) => {
                if (worker) {
                    return (
                        <Space>
                            <CloudServerOutlined style={{ color: '#52c41a' }} />
                            <Text>{worker.name}</Text>
                            <Tag color={worker.status === 'online' ? 'success' : 'error'}>
                                {worker.status === 'online' ? 'Online' : 'Offline'}
                            </Tag>
                            <Button 
                                type="link" 
                                size="small"
                                onClick={() => handleSelectWorker(record)}
                                title="Thay đổi worker"
                            >
                                Change
                            </Button>
                        </Space>
                    );
                }
                
                return (
                    <Button 
                        type="dashed" 
                        size="small" 
                        icon={<CloudServerOutlined />}
                        onClick={() => handleSelectWorker(record)}
                    >
                        Assign Worker
                    </Button>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Test Connection">
                        <Button
                            type="text"
                            size="small"
                            icon={<LinkOutlined />}
                            loading={testingConnection === record.id}
                            onClick={() => handleTestConnection(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <Button 
                    type="primary" 
                    icon={<PlusOutlined />} 
                    onClick={handleAdd}
                >
                    Add Shopify Store
                </Button>
            </div>
            
            <Table
                columns={columns}
                dataSource={storesArray}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title={editingStore ? 'Edit Shopify Store' : 'Add Shopify Store'}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSave}
                >
                    <Form.Item
                        label="Store Name"
                        name="storeName"
                        rules={[{ required: true, message: 'Please enter store name' }]}
                    >
                        <Input placeholder="My Shopify Store" />
                    </Form.Item>

                    <Form.Item
                        label="Shopify Domain"
                        name="shopifyDomain"
                        rules={[
                            { required: true, message: 'Please enter Shopify domain' },
                            { pattern: /^[a-z0-9-]+\.myshopify\.com$/, message: 'Invalid Shopify domain format' }
                        ]}
                    >
                        <Input placeholder="yourstore.myshopify.com" />
                    </Form.Item>

                    <Form.Item
                        label="Store Email"
                        name="storeEmail"
                        rules={[{ type: 'email', message: 'Invalid email format' }]}
                    >
                        <Input placeholder="store@example.com" />
                    </Form.Item>

                    <Form.Item
                        label="Access Token"
                        name="accessToken"
                        rules={[{ required: true, message: 'Please enter access token' }]}
                        extra={
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Custom App access token from Shopify Admin. 
                                Required for API access. Will be encrypted when stored.
                            </Text>
                        }
                    >
                        <Input.Password placeholder="shpat_xxxxx" />
                    </Form.Item>

                    <Form.Item
                        label="API Key"
                        name="apiKey"
                    >
                        <Input placeholder="API Key (optional)" />
                    </Form.Item>

                    <Form.Item
                        label="API Secret Key"
                        name="apiSecretKey"
                    >
                        <Input.Password placeholder="API Secret Key (optional)" />
                    </Form.Item>

                    <Form.Item
                        label="Auto Sync Orders"
                        name="autoSyncOrders"
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        label="Auto Sync Products"
                        name="autoSyncProducts"
                        valuePropName="checked"
                        initialValue={false}
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        label="Sync Interval (minutes)"
                        name="syncInterval"
                        initialValue={15}
                    >
                        <Input type="number" min={5} max={1440} />
                    </Form.Item>

                    <Form.Item
                        label="Active"
                        name="isActive"
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Assign Worker to Shopify Store"
                open={isWorkerModalVisible}
                onCancel={() => {
                    setIsWorkerModalVisible(false);
                    workerForm.resetFields();
                }}
                onOk={() => workerForm.submit()}
                width={500}
            >
                <Form
                    form={workerForm}
                    layout="vertical"
                    onFinish={handleWorkerAssignment}
                >
                    <Form.Item
                        label="Select Worker"
                        name="workerId"
                        rules={[{ required: true, message: 'Please select a worker' }]}
                    >
                        <Select
                            placeholder="Choose a worker"
                            showSearch
                            optionFilterProp="children"
                        >
                            {(workers || []).map(worker => (
                                <Select.Option key={worker.id} value={worker.id}>
                                    <Space>
                                        <CloudServerOutlined />
                                        <span>{worker.name}</span>
                                        <Tag color={worker.status === 'online' ? 'success' : 'error'}>
                                            {worker.status === 'online' ? 'Online' : 'Offline'}
                                        </Tag>
                                    </Space>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Typography.Text type="secondary">
                        Worker này sẽ xử lý các job đồng bộ products/orders cho Shopify store.
                    </Typography.Text>
                </Form>
            </Modal>
        </div>
    );
};

export default ShopifyStoreSettings;
